// Route assignment-edit hook (D3j). Sibling to useLogisticsRouteDetail
// (route + stops fetch), useLogisticsRouteStateTransitions (state-machine
// hops), useLogisticsRouteStopEdits (per-stop quick + admin edits).
// Scope: rig + driver_name reassignment. Carrier-change / scheduled-for /
// notes are deliberately deferred -- the user's stated need is "the
// driver can be overwritten at time of scheduling" and "swap rigs at
// the last minute"; broader assignment edits are a follow-on slice.
//
// Loads the carrier's active rigs on demand (when editing starts) so
// the picker shows the right options. If the route has no carrier yet,
// editing is disabled (rig can't ride without carrier).
import { useCallback, useEffect, useState } from "react";
import {
  listLogisticsCarrierRigs,
  updateLogisticsRoute,
  type LogiCarrierRig,
  type LogiRoute,
} from "@/lib/logistics";

export function useLogisticsRouteAssignmentEdit(
  route: LogiRoute | null,
  onSaved: () => Promise<void> | void
) {
  const [editing, setEditing] = useState(false);
  const [rigsLoading, setRigsLoading] = useState(false);
  const [rigs, setRigs] = useState<LogiCarrierRig[]>([]);
  const [draftRigId, setDraftRigId] = useState<string>("");
  const [draftDriverName, setDraftDriverName] = useState<string>("");
  const [submitBusy, setSubmitBusy] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);

  // Reset draft whenever the selected route changes (a different route's
  // assignment shouldn't bleed into this one).
  useEffect(() => {
    setEditing(false);
    setSubmitError(null);
    setDraftRigId(route?.rig_id ?? "");
    setDraftDriverName(route?.driver_name ?? "");
  }, [route?.id, route?.rig_id, route?.driver_name]);

  // Load rigs the first time editing starts (or when the carrier changes
  // beneath us). Inactive rigs are hidden -- the user shouldn't be able
  // to assign a parked rig. The currently-assigned rig is always kept
  // visible even if it became inactive after assignment, so the user
  // sees what's actually on the row.
  useEffect(() => {
    if (!editing || !route?.carrier_id) {
      setRigs([]);
      return;
    }
    let cancelled = false;
    setRigsLoading(true);
    void (async () => {
      const res = await listLogisticsCarrierRigs({
        carrier_id: route.carrier_id!,
        is_active: true,
      });
      if (cancelled) return;
      setRigsLoading(false);
      if (!res.ok) {
        setRigs([]);
        return;
      }
      const list = Array.isArray(res.data.rigs) ? res.data.rigs : [];
      setRigs([...list].sort((a, b) => a.designation.localeCompare(b.designation)));
    })();
    return () => {
      cancelled = true;
    };
  }, [editing, route?.carrier_id]);

  const startEdit = useCallback(() => {
    setEditing(true);
    setSubmitError(null);
    setDraftRigId(route?.rig_id ?? "");
    setDraftDriverName(route?.driver_name ?? "");
  }, [route?.rig_id, route?.driver_name]);

  const cancelEdit = useCallback(() => {
    setEditing(false);
    setSubmitError(null);
  }, []);

  // Picking a rig in edit mode auto-fills the driver field from the rig's
  // standard driver IF the driver field is currently blank. Once the user
  // has typed a name, we don't clobber their override on re-pick. Same
  // policy as the create-form (useLogisticsRoutesPanel.setNewRigId).
  const setDraftRig = useCallback(
    (rigId: string) => {
      setDraftRigId(rigId);
      if (!rigId) return;
      const picked = rigs.find((r) => r.id === rigId);
      if (picked && picked.standard_driver_name) {
        setDraftDriverName((prev) => prev.trim() || picked.standard_driver_name!);
      }
    },
    [rigs]
  );

  const submit = useCallback(async () => {
    if (!route) return;
    setSubmitBusy(true);
    setSubmitError(null);
    const trimmedDriver = draftDriverName.trim();
    const trimmedRig = draftRigId.trim();
    // Three-way: null = clear, undefined = leave alone, string = set.
    // We send both fields together because the user might be clearing
    // the rig (back to "no rig yet") with the same submit.
    const res = await updateLogisticsRoute({
      route_id: route.id,
      rig_id: trimmedRig ? trimmedRig : null,
      driver_name: trimmedDriver ? trimmedDriver : null,
    });
    setSubmitBusy(false);
    if (!res.ok) {
      setSubmitError(res.error);
      return;
    }
    setEditing(false);
    await onSaved();
  }, [route, draftRigId, draftDriverName, onSaved]);

  return {
    editing,
    rigsLoading,
    rigs,
    draftRigId,
    setDraftRigId: setDraftRig,
    draftDriverName,
    setDraftDriverName,
    submitBusy,
    submitError,
    startEdit,
    cancelEdit,
    submit,
    canEdit: !!route?.carrier_id,
  };
}
